import { createBrowserRouter, Navigate } from 'react-router-dom'
import {
  SignInPage,
  WelcomePage,
  SignupNamePage,
  SignupCpfPage,
  SignupCpfTakenPage,
  SignupEmailPage,
  SignupCodePage,
  SignupPasswordPage,
  ProfileAreaPage,
  ProfileCrmPage,
  ProfileSituationPage,
  ProfileGraduationPage,
  ProfileOriginPage,
  PasswordRecoveryPage,
  CreatePasswordPage,
  ConfirmEmailPage,
  SessionPage,
} from '@/pages/session'
import {
  HiringPage,
  MainPartnerPage,
  DocumentsPage,
  ProposalPage,
  PreContractPage,
  HiringCompanyPage,
  ContractPage,
} from '@/pages/hiring'
import {
  MonthlyDocumentsPage,
  MonthlyDocumentsUploadPage,
} from '@/pages/monthly-documents'
import { FiscalNotesPage, FiscalNoteIssuePage } from '@/pages/fiscal-notes'
import { HomePage } from '@/pages/home/home.page'
import { homeLoader } from '@/pages/home/router-loader'
import { DashboardPage } from '@/pages/dashboard/dashboard.page'
import { RevenuePage } from '@/pages/revenue/revenue.page'
import { TaxesPage } from '@/pages/taxes/taxes.page'
import { AccountPage } from '@/pages/account/account.page'
import { ErrorPage } from '@/pages/error/error.page'
import { AdditionalServicesPage } from '@/pages/additional-services/additional-services.page'
import { MenuPage } from '@/pages/menu/menu.page'

import { ComingSoon, PrivateRoute } from '@/components'
import { CreditApplicationPage } from '@/pages/credit/journeys/credit-application.page'
import { LegacyAccountingRedirect } from '@/components/legacy-accounting-redirect'
import { AppLayout, BaseLayout } from '@/components/layouts'
import { ProductContextProvider } from '@/context/product'
import { CreditHomePage } from '@/pages/credit/credit-home.page'
import { CreditApplicationsPage } from '@/pages/credit/applications/applications.page'
import { ProductDetailPage } from '@/pages/credit/products/product-detail.page'
import { ReferAndEarnPage } from '@/pages/refer-and-earn'
import { BillingPage } from '@/pages/billing/billing.page'
import { MaintenancePage } from './pages/maintenance/maintenance.page'
import { InvitePage } from '@/pages/invite/invite.page'
import { PartnersPage } from '@/pages/partners/partners.page'
import { PartnerPage } from '@/pages/partners/partner/partner.page'
import { PartnerDataPage } from '@/pages/partners/partner/partner-data/partner-data.page'
import { PartnerDependantsPage } from '@/pages/partners/partner/dependents/dependents.page'
import { CollaboratorsPage } from '@/pages/collaborators/collaborators.page'
import { InsurancePage } from '@/pages/insurance'
import { RcpQuotePage } from '@/pages/insurance/rcp-quote'
import { routes } from './routes'

export { routes }

export const maintenanceRouter = createBrowserRouter([
  {
    path: '*',
    element: <MaintenancePage />,
  },
])

export const router = createBrowserRouter([
  {
    element: <SessionPage />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/session',
        element: <WelcomePage />,
      },
      {
        path: '/session/welcome',
        element: <WelcomePage />,
      },
      {
        path: '/session/signin',
        element: <SignInPage />,
      },
      {
        path: '/session/signup',
        element: <SignupNamePage />,
      },
      {
        path: '/session/signup/cpf',
        element: <SignupCpfPage />,
      },
      {
        path: '/session/signup/cpf-taken',
        element: <SignupCpfTakenPage />,
      },
      {
        path: '/session/signup/email',
        element: <SignupEmailPage />,
      },
      {
        path: '/session/signup/code',
        element: <SignupCodePage />,
      },
      {
        path: '/session/password-recovery',
        element: <PasswordRecoveryPage />,
      },
      {
        path: '/session/new-password',
        element: <CreatePasswordPage isRecovery={true} />,
      },
      {
        element: <PrivateRoute />,
        children: [
          {
            path: '/session/create-password',
            element: <Navigate to={routes.session.signup.password} replace />,
          },
          {
            path: '/session/signup/password',
            element: <SignupPasswordPage />,
          },
          {
            path: '/session/confirm-email',
            element: <ConfirmEmailPage />,
          },
          {
            path: routes.session.profile.area,
            element: <ProfileAreaPage />,
          },
          {
            path: routes.session.profile.crm,
            element: <ProfileCrmPage />,
          },
          {
            path: routes.session.profile.situation,
            element: <ProfileSituationPage />,
          },
          {
            path: routes.session.profile.graduation,
            element: <ProfileGraduationPage />,
          },
          {
            path: routes.session.profile.origin,
            element: <ProfileOriginPage />,
          },
        ],
      },
    ],
  },
  {
    element: <PrivateRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <ProductContextProvider />,
        children: [
          {
            element: <BaseLayout />,
            children: [
              {
                path: '/',
                element: <HomePage />,
                loader: homeLoader,
              },
            ],
          },
          {
            path: routes.accounting.hiring.index,
            element: <HiringPage />,
            children: [
              {
                path: routes.accounting.hiring.proposal,
                element: <ProposalPage />,
              },
              {
                path: routes.accounting.hiring.preContract,
                element: <PreContractPage />,
              },
              {
                path: routes.accounting.hiring.mainPartner,
                element: <MainPartnerPage />,
              },
              {
                path: routes.accounting.hiring.company,
                element: <HiringCompanyPage />,
              },
              {
                path: routes.accounting.hiring.contract,
                element: <ContractPage />,
              },
              {
                path: routes.accounting.hiring.documents,
                element: <DocumentsPage />,
              },
            ],
          },
          {
            element: <AppLayout vertical="accounting" />,
            children: [
              {
                path: routes.accounting.index,
                element: <DashboardPage />,
              },
              {
                path: routes.accounting.revenue,
                element: <RevenuePage />,
              },
              {
                path: routes.accounting.fiscalNotes.index,
                element: <FiscalNotesPage />,
              },
              {
                path: routes.accounting.fiscalNotes.issue,
                element: <FiscalNoteIssuePage />,
              },
              {
                path: routes.accounting.taxes,
                element: <TaxesPage />,
              },
              {
                path: routes.accounting.monthlyDocuments,
                element: <MonthlyDocumentsPage />,
              },
              {
                path: routes.accounting.monthlyDocumentsUpload,
                element: <MonthlyDocumentsUploadPage />,
              },
              {
                path: routes.accounting.additionalServices,
                element: <AdditionalServicesPage />,
              },
              {
                path: routes.accounting.insurance,
                element: <InsurancePage />,
              },
              {
                path: routes.accounting.insuranceRcpQuote,
                element: <RcpQuotePage />,
              },
              {
                path: routes.accounting.referAndEarn,
                element: <ReferAndEarnPage />,
              },
              {
                path: routes.accounting.billing,
                element: <BillingPage />,
              },
              {
                path: routes.accounting.menu,
                element: <MenuPage />,
              },
              {
                path: routes.accounting.collaborators,
                element: <CollaboratorsPage />,
              },
              {
                path: routes.accounting.partners.index,
                element: <PartnersPage />,
              },
              {
                path: '/accounting/partners/:partner_key',
                element: <PartnerPage />,
                children: [
                  {
                    path: routes.accounting.partners.data,
                    element: <PartnerDataPage />,
                  },
                  {
                    path: routes.accounting.partners.dependants,
                    element: <PartnerDependantsPage />,
                  },
                ],
              },
            ],
          },
          {
            element: <AppLayout />,
            children: [
              {
                path: routes.account,
                element: <AccountPage />,
              },
            ],
          },
          {
            element: <AppLayout vertical="credit" />,
            children: [
              {
                path: routes.credit.index,
                element: <CreditHomePage />,
              },
              {
                path: routes.credit.applications,
                element: <CreditApplicationsPage />,
              },
              {
                path: routes.credit.application,
                handle: { focus: true },
                element: <CreditApplicationPage />,
              },
              {
                path: routes.credit.product,
                element: <ProductDetailPage />,
              },
              {
                path: routes.credit.installments,
                element: <ComingSoon title="Parcelas" />,
              },
              {
                path: routes.credit.contracts,
                element: <ComingSoon title="Contratos" />,
              },
              {
                path: routes.credit.menu,
                element: <MenuPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/invite/accept/:deal_user_key',
    element: <InvitePage />,
  },
  { path: '/dashboard', element: <LegacyAccountingRedirect /> },
  { path: '/revenue', element: <LegacyAccountingRedirect /> },
  { path: '/revenue/*', element: <LegacyAccountingRedirect /> },
  { path: '/fiscal-notes', element: <LegacyAccountingRedirect /> },
  { path: '/fiscal-notes/*', element: <LegacyAccountingRedirect /> },
  { path: '/taxes', element: <LegacyAccountingRedirect /> },
  { path: '/taxes/*', element: <LegacyAccountingRedirect /> },
  { path: '/monthly-documents', element: <LegacyAccountingRedirect /> },
  { path: '/monthly-documents/*', element: <LegacyAccountingRedirect /> },
  { path: '/additional-services', element: <LegacyAccountingRedirect /> },
  { path: '/additional-services/*', element: <LegacyAccountingRedirect /> },
  { path: '/insurance', element: <LegacyAccountingRedirect /> },
  { path: '/insurance/*', element: <LegacyAccountingRedirect /> },
  { path: '/refer-and-earn', element: <LegacyAccountingRedirect /> },
  { path: '/refer-and-earn/*', element: <LegacyAccountingRedirect /> },
  { path: '/billing', element: <LegacyAccountingRedirect /> },
  { path: '/billing/*', element: <LegacyAccountingRedirect /> },
  { path: '/menu', element: <LegacyAccountingRedirect /> },
  { path: '/menu/*', element: <LegacyAccountingRedirect /> },
  { path: '/partners', element: <LegacyAccountingRedirect /> },
  { path: '/partners/*', element: <LegacyAccountingRedirect /> },
  { path: '/collaborators', element: <LegacyAccountingRedirect /> },
  { path: '/collaborators/*', element: <LegacyAccountingRedirect /> },
  { path: '/hiring', element: <LegacyAccountingRedirect /> },
  { path: '/hiring/*', element: <LegacyAccountingRedirect /> },
  {
    path: '*',
    element: <ErrorPage />,
  },
])
